// The AI team on one simple screen: ask for something, see the numbers,
// decide what's waiting, and open a team to give it a job or see its work.
import { EmptyState } from '@/components/primitives/EmptyState'
import { ErrorState } from '@/components/primitives/ErrorState'
import { Switch } from '@/components/ui/switch'
import {
  type ChatRoomSummary,
  type TeamAgent,
  fetchApprovals,
  fetchRooms,
  fetchTeam,
  sendMessage
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { runRecordAction } from '@/lib/sheet'
import { cn } from '@/lib/utils'
import { AgentAvatar } from '@/pages/ai-team/AgentAvatar'
import { CHIP, HUBS, type Hub, card, hubAgents, money, totals } from '@/pages/ai-team/hubs'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRight,
  ArrowUp,
  BellRing,
  DollarSign,
  Loader2,
  Mail,
  PenSquare,
  Sparkles,
  Users,
  Zap
} from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Decisions, Stat, TeamToggle, goBtn, leadOf, sectionTitle, started, teamPath, teamStatus } from './parts'
import { type ResultRef, ResultList, useResults } from './ResultCard'

export function Overview() {
  const team = useQuery({ queryKey: ['ai-team'], queryFn: fetchTeam, refetchInterval: 30_000 })
  const approvals = useQuery({
    queryKey: ['ai-approvals'],
    queryFn: fetchApprovals,
    refetchInterval: 5_000
  })
  const rooms = useQuery({ queryKey: ['ai-rooms'], queryFn: fetchRooms, refetchInterval: 10_000 })
  const results = useResults('overview')

  if (team.isLoading) return <OverviewSkeleton />
  if (team.error) return <ErrorState error={team.error} onRetry={() => team.refetch()} />
  const data = team.data
  if (!data || data.agents.length === 0)
    return <EmptyState icon={Users} title="No agents yet" description="Agents appear once the backend has synced them." />

  const all = totals(data.agents)
  const items = approvals.data?.items ?? []
  const urgent = items.some((i) => i.severity === 'urgent' || i.money)

  return (
    <div className="space-y-6">
      <AskBar
        agents={data.agents}
        teamRoom={rooms.data?.team ?? null}
        roomsError={rooms.isError && !rooms.data}
        onRetryRooms={() => rooms.refetch()}
        onStarted={results.add}
      />
      <ResultList items={results.items} onDismiss={results.remove} onSent={results.addSent} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Stat label="Needs you" value={items.length} icon={BellRing} tone={items.length === 0 ? 'emerald' : urgent ? 'red' : 'amber'} />
        <Stat label="Working now" value={all.working} icon={Zap} tone="emerald" />
        <Stat label="Runs today" value={all.runs} icon={Sparkles} tone="sky" />
        <Stat label="Failed today" value={all.failed} icon={AlertTriangle} tone={all.failed > 0 ? 'red' : 'neutral'} />
        <Stat
          label={`Spent of ${money(Number(data.budget_today))}`}
          value={Number(data.spent_today)}
          format={money}
          icon={DollarSign}
          tone="emerald"
          progress={Number(data.budget_today) ? Number(data.spent_today) / Number(data.budget_today) : 0}
        />
        <Stat label="Open emails" value={all.emails} icon={Mail} tone="violet" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Decisions items={items} loading={approvals.isLoading} />
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className={sectionTitle}>Your teams</h2>
            <Link
              to="/apps/ai-team/team/content#new-post"
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
            >
              <PenSquare className="size-4" /> New post
            </Link>
            <WholeTeamSwitch on={data.team_on} settingsId={data.settings_id} />
          </div>
          <div className="space-y-3">
            {HUBS.map((h) => (
              <TeamCard key={h.id} hub={h} agents={hubAgents(h, data.agents)} />
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

function AskBar({
  agents,
  teamRoom,
  roomsError,
  onRetryRooms,
  onStarted
}: {
  agents: TeamAgent[]
  teamRoom: ChatRoomSummary | null
  roomsError: boolean
  onRetryRooms: () => void
  onStarted: (r: ResultRef) => void
}) {
  const [text, setText] = useState('')
  const [target, setTarget] = useState('')
  const [pending, setPending] = useState(false)

  async function ask(e: FormEvent) {
    e.preventDefault()
    if (!text.trim() || !teamRoom || pending) return
    setPending(true)
    try {
      // The team room: Chief of Staff answers, or an @mention sends it to that team's lead.
      const hub = HUBS.find((h) => h.id === target)
      const lead = hub ? leadOf(hub, agents) : null
      const sent = await sendMessage(teamRoom.id, lead ? `@${lead.key} ${text.trim()}` : text.trim())
      setText('')
      started(lead?.name ?? 'Chief of Staff')
      onStarted({
        roomId: teamRoom.id,
        afterId: sent.id,
        ids: [sent.id],
        title: text.trim().slice(0, 80),
        agentKey: lead?.key ?? 'chief_of_staff',
        agentName: lead?.name ?? 'Chief of Staff',
        at: new Date().toISOString()
      })
      await queryClient.invalidateQueries({ queryKey: ['ai-rooms'] })
    } catch (err) {
      notifyError('Could not send that', err)
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="space-y-1.5">
    <form
      onSubmit={ask}
      className="flex flex-col gap-2 rounded-[36px] border border-neutral-200 bg-white bg-gradient-to-r from-emerald-500/10 via-transparent to-red-500/10 p-2 sm:flex-row sm:items-center dark:border-neutral-800 dark:bg-black"
    >
      <label className="sr-only" htmlFor="ai-ask">
        What do you need?
      </label>
      <input
        id="ai-ask"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What do you need? e.g. “Refund order 1042” or “Draft this week’s newsletter”"
        className="min-w-0 flex-1 bg-transparent px-4 py-2.5 text-base text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-white dark:placeholder:text-neutral-500"
      />
      <div className="flex gap-2">
        <select
          aria-label="Who should do it"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="min-w-0 flex-1 rounded-full border border-neutral-200 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-900 sm:flex-none dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
        >
          <option value="">Chief of Staff decides</option>
          {HUBS.filter((h) => h.id !== 'executive').map((h) => {
            const off = !hubAgents(h, agents).some((a) => a.enabled)
            return (
              <option key={h.id} value={h.id} disabled={off}>
                {h.title}
                {off ? ' (off)' : ''}
              </option>
            )
          })}
        </select>
        <button type="submit" disabled={pending || !text.trim() || !teamRoom} className={goBtn}>
          {pending ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
          Ask
        </button>
      </div>
    </form>
    {roomsError && (
      <p role="alert" className="flex flex-wrap items-center gap-2 px-4 text-xs font-semibold text-red-600 dark:text-red-400">
        Couldn't reach the team chat, so asking is paused.
        <button type="button" onClick={onRetryRooms} className="rounded-full underline underline-offset-2">
          Try again
        </button>
      </p>
    )}
    </div>
  )
}

function WholeTeamSwitch({ on, settingsId }: { on: boolean; settingsId: number }) {
  const [pending, setPending] = useState(false)
  async function toggle(next: boolean) {
    setPending(true)
    try {
      const r = await runRecordAction('super_agents__agentsettings', settingsId, next ? 'team_on' : 'team_off', {})
      toast.success(r.message || (next ? 'The AI team is on.' : 'The AI team is off.'))
      await queryClient.invalidateQueries({ queryKey: ['ai-team'] })
    } catch (err) {
      notifyError(next ? 'Could not turn the team on' : 'Could not turn the team off', err)
    } finally {
      setPending(false)
    }
  }
  return (
    <label className="flex items-center gap-2 text-sm font-semibold text-neutral-600 dark:text-neutral-300">
      {pending && <Loader2 className="size-3.5 animate-spin" />}
      Whole team {on ? 'on' : 'off'}
      <Switch checked={on} disabled={pending} onCheckedChange={toggle} aria-label="Whole team on or off" />
    </label>
  )
}

function TeamCard({ hub, agents }: { hub: Hub; agents: TeamAgent[] }) {
  const t = totals(agents)
  const status = teamStatus(agents)
  return (
    <div className={cn(card, 'flex flex-wrap items-center gap-3')}>
      <Link to={teamPath(hub)} className="group flex min-w-0 flex-1 items-center gap-3">
        <span className={cn('flex size-11 shrink-0 items-center justify-center rounded-full', CHIP[hub.tone])}>
          <hub.icon className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-neutral-900 dark:text-white">{hub.title}</span>
            <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-bold', CHIP[status.tone])}>
              {status.tone === 'emerald' && (
                <span className="mr-1.5 inline-block size-1.5 rounded-full bg-emerald-500 align-middle motion-safe:animate-pulse" />
              )}
              {status.text}
            </span>
          </span>
          <span className="block truncate text-xs text-neutral-500 dark:text-neutral-400">
            {t.runs} runs today · {money(t.spent)} spent · {agents.length} {agents.length === 1 ? 'agent' : 'agents'}
          </span>
        </span>
        <span className="hidden -space-x-2 sm:flex">
          {agents.slice(0, 4).map((a) => (
            <AgentAvatar key={a.key} agentKey={a.key} name={a.name} size="sm" />
          ))}
        </span>
        <ArrowRight className="size-4 shrink-0 text-neutral-400 transition-transform group-hover:translate-x-0.5" />
      </Link>
      <TeamToggle hub={hub} agents={agents} />
    </div>
  )
}

function OverviewSkeleton() {
  const block = 'animate-pulse rounded-[36px] bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800'
  return (
    <div className="space-y-6">
      <div className={cn(block, 'h-16')} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={cn(block, 'h-[124px]')} />
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className={cn(block, 'h-32')} />
          ))}
        </div>
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={cn(block, 'h-20')} />
          ))}
        </div>
      </div>
    </div>
  )
}
